import React from 'react';
import { Moon, Users, ArrowRight, Shield, Compass, Map } from 'lucide-react';

const DarknessSection = () => {
  return (
    <section className="py-24 bg-gradient-to-b from-slate-900 via-purple-950 to-slate-900 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Section header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm font-medium mb-4 border border-purple-500/30">
            Understanding Darkness
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Shine light into the
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"> darkness</span>
          </h2>
          <p className="text-lg text-slate-400">
            Understand the spiritual landscape and learn to effectively minister to those walking in darkness.
          </p>
        </div>

        {/* Two column cards */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* Land */}
          <div className="group relative">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 hover:bg-white/10 transition-all duration-300">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                  <Map className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-3">Land</h3>
                  <p className="text-slate-400 mb-6 leading-relaxed">
                    Navigate spiritual territories with wisdom and discernment. Understand the landscapes where darkness operates and learn strategic approaches for breakthrough.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Compass className="w-5 h-5 text-purple-400" />
                      <span className="text-slate-300">Territorial understanding</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-purple-400" />
                      <span className="text-slate-300">Spiritual mapping</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Moon className="w-5 h-5 text-purple-400" />
                      <span className="text-slate-300">Strategic intercession</span>
                    </div>
                  </div>
                  <a href="#land" className="inline-flex items-center gap-2 mt-6 text-purple-400 font-medium hover:text-purple-300 group/link">
                    Explore Land <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* People */}
          <div className="group relative">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-cyan-600/20 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <div className="relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 hover:bg-white/10 transition-all duration-300">
              <div className="flex items-start gap-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-white mb-3">People</h3>
                  <p className="text-slate-400 mb-6 leading-relaxed">
                    Learn to effectively minister to those walking in darkness. Understand their needs, speak their language, and guide them toward the light with compassion.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-blue-400" />
                      <span className="text-slate-300">Compassionate outreach</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-blue-400" />
                      <span className="text-slate-300">Effective communication</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Moon className="w-5 h-5 text-blue-400" />
                      <span className="text-slate-300">Light in relationships</span>
                    </div>
                  </div>
                  <a href="#people" className="inline-flex items-center gap-2 mt-6 text-blue-400 font-medium hover:text-blue-300 group/link">
                    Explore People <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <p className="text-slate-400 mb-6">
            &ldquo;The light shines in the darkness, and the darkness has not overcome it.&rdquo; - John 1:5
          </p>
          <button className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-purple-500/25 transition-all">
            Begin Your Understanding
          </button>
        </div>
      </div>
    </section>
  );
};

export default DarknessSection;
